import type {
  LandingInterpretationIntervalRow,
  WellLandingAnalysisInstance,
  WellLandingAnalysisPreview,
} from 'types/api-responses';
import type { WellborePointsResponse } from 'types/wellbore-geometry-workspace';
import { parseUTCdateTime } from 'utils/dates';
import type {
  LandingAvailability,
  LandingBanner,
  LandingCollectionOption,
  LandingIntervalCard,
} from './types';

// 'WOLFCAMP A' to 'Wolfcamp A': intervals are stack-uppercase, the banners read in title case.
export const titleCase = (name: string): string =>
  name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

// Which state the section is in (INS-12936). Depths gate everything: a geometry carrying plan
// positions only offers no pick. Otherwise a bore with prior runs reads them (`ready`), and one
// without — no geometry, or depths but no analysis yet, the common production case — is
// attributed by hand from the catalog (`interval-only`).
export const landingAvailability = ({
  points,
  pointsHaveDepths,
  runCount,
}: {
  points: WellborePointsResponse | null;
  pointsHaveDepths: boolean;
  runCount: number;
}): LandingAvailability => {
  const hasGeometry = (points?.points?.length ?? 0) > 0;
  if (hasGeometry && !pointsHaveDepths) return 'no-depths';
  return runCount > 0 ? 'ready' : 'interval-only';
};

export const isManualRun = (
  run: Pick<WellLandingAnalysisInstance, 'analysisType'>
) => !!run.analysisType && run.analysisType !== 'system_landing_analysis';

// Listed by date of analysis: that is what tells one run of the same well from another.
export const runLabel = (run: WellLandingAnalysisInstance): string =>
  parseUTCdateTime(run.createdAt, true);

// Planned geometries an operator authors in a workspace (WellboreGeometrySourceType::
// WORKSPACE_BACKED_TYPES): a run on one belongs to that workspace's reading.
const WORKSPACE_BACKED_SOURCES = ['pre_drill', 'synthetic'];

// The geometry a run was computed on, for the picker (review item 7.2): the drilled survey, the
// workspace whose planned geometry it ran on, or a system-owned estimate.
export const runSourceLabel = (run: WellLandingAnalysisInstance): string => {
  switch (run.geometrySourceType) {
    case 'source_well_survey':
      return 'Drilled survey';
    case 'estimated':
      return 'Estimated geometry';
    case 'well_stick':
      return 'Well stick';
    case 'pre_drill':
    case 'synthetic':
      return run.workspace?.name ?? 'Planned geometry';
    default:
      return '';
  }
};

// The runs this bore's interpretation may read from (review item 7.2): those on its drilled survey
// or another system-owned geometry, or on THIS geometry workspace's own planned geometry. Another
// workspace's planned runs are its own reading and are not offered. Manual runs stay listed (they
// are this bore's interpretations, labelled Manual). The backend validates a Mark as Correct source
// against the same rule (WellLandingAnalysis.offered_for).
export const offeredRuns = (
  runs: WellLandingAnalysisInstance[],
  geometryWorkspaceId: number | null
): WellLandingAnalysisInstance[] =>
  runs.filter(
    (run) =>
      isManualRun(run) ||
      !WORKSPACE_BACKED_SOURCES.includes(run.geometrySourceType ?? '') ||
      (geometryWorkspaceId !== null &&
        run.workspace?.id === geometryWorkspaceId)
  );

// The server's message for a failed write, when it sent one: a list of { message } (the bore
// address's coded errors) or a field => messages map.
export const writeErrorMessage = (error: unknown): string | null => {
  const errors = (error as { errors?: unknown } | null | undefined)?.errors;
  if (Array.isArray(errors)) {
    const messages = errors
      .map((entry) => (entry as { message?: string })?.message)
      .filter((message): message is string => !!message);
    return messages.length > 0 ? messages.join(' ') : null;
  }
  if (errors && typeof errors === 'object') {
    const messages = Object.values(errors as Record<string, string[]>).flat();
    return messages.length > 0 ? messages.join(' ') : null;
  }
  return null;
};

const NO_COLLECTION_ID = 0;

// The collections the bore's runs were read against, the default first. A manual interval-only
//  run carries none and is grouped under "No Structure Grid Collection".
export const collectionOptions = (
  runs: WellLandingAnalysisInstance[]
): LandingCollectionOption[] => {
  const byId = new Map<number, LandingCollectionOption>();
  runs.forEach((run) => {
    const collection = run.structureGridCollection;
    const id = collection?.id ?? NO_COLLECTION_ID;
    if (byId.has(id)) return;
    byId.set(id, {
      id,
      name: collection?.collectionName ?? 'No Structure Grid Collection',
      isDefault: collection?.isDefault ?? false,
    });
  });
  return [...byId.values()].sort(
    (a, b) =>
      Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name)
  );
};

export const runsInCollection = (
  runs: WellLandingAnalysisInstance[],
  collectionId: number | null
) =>
  runs
    .filter(
      (run) =>
        (run.structureGridCollection?.id ?? NO_COLLECTION_ID) === collectionId
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

// One card per interval the lateral passes through, biggest share first. The run's own zone wears
// the Current pill. A catalog pick outside that shortlist is appended as its own card so the
// staged reading is always visible.
export const intervalCards = ({
  run,
  intervals,
  stagedId,
}: {
  run: WellLandingAnalysisPreview | null;
  intervals: LandingInterpretationIntervalRow[];
  stagedId: number | null;
}): LandingIntervalCard[] => {
  const countFor = (id: number) =>
    intervals.find((interval) => interval.id === id)?.nearbyLandedWellCount ??
    null;
  const currentName =
    run?.wellLevel?.landingZoneInterval?.toUpperCase() ?? null;
  const cards: LandingIntervalCard[] = (run?.allInZones ?? [])
    .filter((zone) => zone.percentInZone > 0 && zone.geologicIntervalId)
    .sort((a, b) => b.percentInZone - a.percentInZone)
    .map((zone) => ({
      id: zone.geologicIntervalId,
      name: zone.geologicInterval,
      percentInZone: zone.percentInZone,
      nearbyLandedWells: countFor(zone.geologicIntervalId),
      gridSurface: zone.gridName ?? null,
      isCurrent: zone.geologicInterval?.toUpperCase() === currentName,
    }));
  if (stagedId !== null && !cards.some((card) => card.id === stagedId)) {
    const picked = intervals.find((interval) => interval.id === stagedId);
    if (picked) cards.push(catalogCard(picked));
  }
  return cards;
};

// A card for an interval picked from the catalog: no analysis reading, so no Percent in Zone.
export const catalogCard = (
  interval: LandingInterpretationIntervalRow
): LandingIntervalCard => ({
  id: interval.id,
  name: interval.geologicInterval,
  percentInZone: null,
  nearbyLandedWells: interval.nearbyLandedWellCount,
  gridSurface: interval.gridSurface,
  isCurrent: false,
});

// The run's own zone, by interval id.
export const currentIntervalId = (
  run: WellLandingAnalysisPreview | null
): number | null => {
  const name = run?.wellLevel?.landingZoneInterval?.toUpperCase();
  if (!name) return null;
  return (
    run?.allInZones.find(
      (zone) => zone.geologicInterval?.toUpperCase() === name
    )?.geologicIntervalId ?? null
  );
};

// The one banner the section shows (narrative "Banners" table, copy verbatim). The states are
// mutually exclusive and, within each, the banner advances as a reading is staged and applied.
export const landingBanner = ({
  availability,
  appliedName,
  staged,
  zoneChanged,
}: {
  availability: LandingAvailability;
  /** The interval marked correct, or null. */
  appliedName: string | null;
  /** A pick is staged but not marked correct. */
  staged: boolean;
  /** The staged (or applied) zone differs from the run's own. */
  zoneChanged: boolean;
}): LandingBanner => {
  if (availability === 'no-depths') {
    return {
      kind: 'info',
      title: 'This wellbore geometry has no associated depths.',
      subtitle:
        'Recalculate the geometry from a Target Zone selection, or from the Permitted TVD, to derive them. The landing analysis runs automatically once depths exist, and the interpretation unlocks here.',
    };
  }
  if (availability === 'interval-only') {
    if (appliedName) {
      return {
        kind: 'success',
        title: `Landing interval marked correct: ${titleCase(appliedName)}.`,
        subtitle:
          'A new manual landing analysis and interpretation will be created on save and workspace promote. This does not drive the actual wellbore geometry. Remove correct status to change the interval.',
      };
    }
    if (staged) {
      return {
        kind: 'warning',
        title: 'Landing interval selected.',
        subtitle:
          "Mark as Correct to apply it as this wellbore's landing interval. Nothing is assigned until it is marked correct.",
      };
    }
    return {
      kind: 'info',
      title: 'No landing analysis exists.',
      subtitle:
        'To assign an interpreted landing interval to this wellbore, select a geologic interval below. Reminder: the interpretation does not drive the actual wellbore geometry.',
    };
  }
  if (appliedName) {
    return {
      kind: 'success',
      title: `Landing zone marked correct: ${titleCase(appliedName)}.`,
      subtitle: zoneChanged
        ? 'A new manual landing analysis will be created on save and workspace promote. This does not edit the underlying wellbore geometry or wellbore location, only the landing zone interpretation. Remove correct status to change it.'
        : "The analysis's own interpretation is applied as this wellbore's landing zone. Remove correct status to change it.",
    };
  }
  if (staged && zoneChanged) {
    return {
      kind: 'warning',
      title: 'Landing zone changed.',
      subtitle:
        'Mark as Correct to apply it. A new manual landing analysis will then be created on save and workspace promote. This never edits the underlying wellbore geometry or wellbore location, only the landing zone interpretation.',
    };
  }
  return {
    kind: 'info',
    title: 'Select a landing analysis to see its default interpretation.',
    subtitle:
      'Mark the interpretation as correct to apply it, or select a different landing zone to generate a new manual landing analysis.',
  };
};
